import { computed, inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { forkJoin, map } from 'rxjs';
import { RsOverviewServerSideStateManager } from '@rs-shared-lib/abstract-classes/rs-overview-state/rs-overview-server-side-state-manager';
import { RsPersistOverviewState } from '@rs-shared-lib/abstract-classes/rs-overview-state/persistency/rs-persist-overview-state.decorator';
import { rsCountFilters } from '@rs-shared-lib/utils/count-filters/count-filters';
import { ServerSideQueryParams } from '@rs-shared-lib/abstract-classes/rs-overview-state/models/rs-overview-server-side-state-manager';
import { UserService } from '@core/services/user.service';
import { TranslationService, RsUtils } from '@lib/rs-ngx';
import { environment } from '@environments/environment';
import { MRT_DEFAULT_PAGE_SIZE, MRT_PAGE_SIZE_OPTIONS } from '@shared/constants/mrt-page-size-options';
import { LeasingCompanyThresholdFilters } from '@app/leasing-company/leasing-company-thresholds/shared/models/leasing-company-threshold-parameters';
import { LeasingCompanyThresholdSummary } from '@shared/models/leasing-company-threshold/leasing-company-threshold-summary';

@RsPersistOverviewState({
  storageKeySuffix: 'MrtLeasingCompanyThresholds',
  includeUserContext: () => inject(UserService).getCurrentUser()!.loginString,
})
@Injectable()
export class LeasingCompanyThresholdsOverviewStateService extends RsOverviewServerSideStateManager<LeasingCompanyThresholdSummary, LeasingCompanyThresholdFilters> {
  public readonly filterCount = computed(() => rsCountFilters(this.filters()));

  constructor() {
    const http = inject(HttpClient);
    const currentLanguage = inject(TranslationService).currentLang;

    super({
      serviceMethod: (params: ServerSideQueryParams<LeasingCompanyThresholdFilters>) => {
        const {
          _page, _pageSize, _sortBy, _sortDirection, ...restFilters
        } = params;
        const filters = RsUtils.trimEmptyHttpParams({
          ...restFilters,
          page: _page,
          resultsPerPage: _pageSize,
          sortBy: _sortBy,
          sortDirection: _sortDirection,
          userLanguage: currentLanguage(),
        });

        return forkJoin([
          http.get<LeasingCompanyThresholdSummary[]>(`${environment.apiUrl}/leasing-company/threshold`, { params: filters }),
          http.get<number>(`${environment.apiUrl}/leasing-company/threshold/pagecount`, { params: filters }),
        ]).pipe(
          map(([data, pageCount]) => new HttpResponse<LeasingCompanyThresholdSummary[]>({
            body: data,
            headers: new HttpHeaders({
              resultcount: String(pageCount * _pageSize),
              pagecount: String(pageCount),
            }),
          })),
        );
      },
      initialSortingState: { active: 'ACTIVITY_DESCRIPTION', direction: 'asc' },
      initialFiltersState: {
        activityCodeFrom: '',
        activityCodeTo: '',
        activityDescription: '',
        febiacBrandCode: '',
        firstRegistrationStartingFrom: '',
        febiacCylinderContent: '',
        febiacFuelCode: '',
        febiacModel: '',
        febiacPower: '',
        febiacTransmission: '',
        leasingContractGroupId: null,
        thresholdType: null,
        supplierGroupId: null,
      },
      initialPagination: { pageIndex: 0, pageSize: MRT_DEFAULT_PAGE_SIZE },
      config: { pageSizeOptions: MRT_PAGE_SIZE_OPTIONS },
    });
  }
}
